import { logger } from '@gem/modules/logger';

import { secretChatAccessRequestStatusResponseMap } from 'APP/model/e2ee/e2eeModel.mapper';
import { mapChatRole } from 'APP/model/group/groupModel.b2b.mapper';
import { mapIMessage } from 'APP/model/message/messageModel.b2b.mapper';
import {
  ChatEvent,
  ChatMarkedAsUnreadPayload,
  ChatMessagesTtlChangedPayload,
  ChatUpdatedEventData,
  DeleteMessagesEventPayload,
  SecretChatAccessRequestUpdatePayload,
  UpdateMessageEventPayload,
  UserChatsRolesChangedEventPayload,
  UserPinChatEventPayload,
  UserScheduledMessageDeletedPayload,
  UserScheduledMessageUpdatedPayload,
  UserStickerPacksUpdatedEventPayload,
} from 'APP/wsApi/wsAppApi/types/chat/chat_event_model_pb';
import { ChatUpdate } from 'APP/wsApi/wsAppApi/types/chat/chat_update_model_pb';

import {
  EventType,
  IChatMarkedAsUnreadEventPayloadResponse,
  IChatMessagesTtlChangedPayloadResponse,
  IDeleteMessagesEventPayloadResponse,
  IDeleteScheduledMessageEventPayloadResponse,
  IGroupCounterView,
  IGroupEvent,
  IGroupEventPayloadResponse,
  IPinGroupEventPayloadResponse,
  ISecretChatAccessRequestUpdatePayloadResponse,
  IUpdateMessageEventPayloadResponse,
  IUpdateScheduledMessageEventPayloadResponse,
  IUserGroupRoleChanged,
  IUserStickerPacksUpdatedEventPayloadResponse,
} from './counterModel.types';

export function mapChatEventGeneral(
  chatEvent: ChatEvent
): Omit<IGroupEvent, 'type' | 'eventPayload'> {
  return {
    id: chatEvent.id.toString(),
    groupId: chatEvent.chatId.toString(),
    ts: Number(chatEvent.ts),
  };
}

export function mapDeleteMessagesPayload(
  payload: DeleteMessagesEventPayload
): IDeleteMessagesEventPayloadResponse {
  return {
    groupId: payload.chatId.toString(),
    messageIds: payload.messageIds.map((messageId) => messageId.toString()),
  };
}

export function mapChatUpdatedload(payload?: ChatUpdatedEventData): IGroupEventPayloadResponse {
  return {
    groupId: payload?.chatId.toString() || '',
  };
}

export function mapPinChatPayload(payload: UserPinChatEventPayload): IPinGroupEventPayloadResponse {
  return {
    groupIds: payload.chatIds.map((chatId) => chatId.toString()),
  };
}

export function mapRolesChangedPayload(
  payload: UserChatsRolesChangedEventPayload
): IUserGroupRoleChanged[] {
  return payload.chatsRolesChanges.reduce<IUserGroupRoleChanged[]>((result, item) => {
    if (item?.chatId && item?.oldRole !== undefined && item?.newRole !== undefined) {
      result.push({
        oldRole: mapChatRole(item.oldRole),
        newRole: mapChatRole(item.newRole),
        groupId: String(item.chatId),
      });
    }
    return result;
  }, []);
}

export function mapUpdateMessagePayload(
  payload: UpdateMessageEventPayload
): IUpdateMessageEventPayloadResponse {
  return {
    messageGroup: payload.chatId.toString(),
    messageId: payload.messageId.toString(),
  };
}

export function mapUpdateScheduledMessagePayload(
  payload: UserScheduledMessageUpdatedPayload
): IUpdateScheduledMessageEventPayloadResponse {
  return {
    groupId: payload.chatId.toString(),
    messageId: payload.messageId.toString(),
    status: payload.status,
  };
}

export function mapDeleteScheduledMessagePayload(
  payload: UserScheduledMessageDeletedPayload
): IDeleteScheduledMessageEventPayloadResponse {
  return {
    groupId: payload.chatId.toString(),
    messageIds: [payload.messageId.toString()],
  };
}

export function mapSecretChatAccessRequestUpdatePayload(
  payload: SecretChatAccessRequestUpdatePayload
): ISecretChatAccessRequestUpdatePayloadResponse {
  return {
    userId: payload.userId.toString(),
    deviceId: payload.deviceId,
    status: secretChatAccessRequestStatusResponseMap[payload.status],
    activeRequestsCount: payload.activeRequestsCount,
  };
}

export function mapUserStickerPacksUpdatedPayload(
  payload: UserStickerPacksUpdatedEventPayload
): IUserStickerPacksUpdatedEventPayloadResponse {
  return {
    stickerPacks: payload.stickerPacks.map((item) => item.toString()),
  };
}

export function mapChatMarkedAsUnreadPayload(
  payload: ChatMarkedAsUnreadPayload
): IChatMarkedAsUnreadEventPayloadResponse {
  return {
    groupId: payload.chatId.toString(),
    unread: payload.unread,
  };
}

export function mapChatMessagesTtlChangedPayload(
  payload: ChatMessagesTtlChangedPayload
): IChatMessagesTtlChangedPayloadResponse {
  return {
    ttlPeriodMs: payload.periodMs ? Number(payload.periodMs) : 0,
  };
}

export function mapChatEvent(chatEvent: ChatEvent): IGroupEvent | undefined {
  const generalData = mapChatEventGeneral(chatEvent);
  if (!chatEvent.payload?.type.case) {
    logger.get('API').error('Unknown chat event type', chatEvent);
    return;
  }

  switch (chatEvent.payload?.type.case) {
    case 'userScheduledMessageUpdated': {
      return {
        ...generalData,
        type: EventType.ScheduledMessageWasUpdated,
        eventPayload: mapUpdateScheduledMessagePayload(chatEvent.payload.type.value),
      };
    }
    case 'userScheduledMessageDeleted': {
      return {
        ...generalData,
        type: EventType.ScheduledMessagesWereDeleted,
        eventPayload: mapDeleteScheduledMessagePayload(chatEvent.payload.type.value),
      };
    }
    case 'secretChatAccessRequestUpdate': {
      return {
        ...generalData,
        type: EventType.SecretChatAccessRequestUpdate,
        eventPayload: mapSecretChatAccessRequestUpdatePayload(chatEvent.payload.type.value),
      };
    }
    case 'deleteMessages':
      return {
        ...generalData,
        type: EventType.MessagesWereDeleted,
        eventPayload: mapDeleteMessagesPayload(chatEvent.payload.type.value),
      };
    case 'chatChanged':
      return {
        ...generalData,
        type: EventType.GroupChanged,
        eventPayload: { groupId: generalData.groupId },
      };
    case 'userChatChanged':
      return {
        ...generalData,
        type: EventType.GroupChanged,
        eventPayload: mapChatUpdatedload(chatEvent.payload.type.value.eventData),
      };
    case 'userClearChatHistory':
      return {
        ...generalData,
        type: EventType.ClearHistory,
        eventPayload: mapChatUpdatedload(chatEvent.payload.type.value.eventData),
      };
    case 'clearChatHistory':
      return {
        ...generalData,
        type: EventType.ClearHistoryForAll,
      };
    case 'userLeftChat':
      return {
        ...generalData,
        type: EventType.GroupSelfLeave,
        eventPayload: mapChatUpdatedload(chatEvent.payload.type.value.eventData),
      };
    case 'userChatWasDeleted':
      return {
        ...generalData,
        type: EventType.GroupWasDeleted,
        eventPayload: mapChatUpdatedload(chatEvent.payload.type.value.eventData),
      };
    case 'userWasRemoved':
      return {
        ...generalData,
        type: EventType.UserWasRemoved,
        eventPayload: mapChatUpdatedload(chatEvent.payload.type.value.eventData),
      };
    case 'userPinChat':
      return {
        ...generalData,
        type: EventType.ConfigGroupPin,
        eventPayload: mapPinChatPayload(chatEvent.payload.type.value),
      };
    case 'userChatsRolesChanged':
      return {
        ...generalData,
        type: EventType.RolesWereChanged,
        eventPayload: mapRolesChangedPayload(chatEvent.payload.type.value),
      };
    case 'updateMessage':
      return {
        ...generalData,
        type: EventType.MessageWasUpdated,
        eventPayload: mapUpdateMessagePayload(chatEvent.payload.type.value),
      };
    case 'userStickerPacksUpdated':
      return {
        ...generalData,
        type: EventType.UserStickerPacksUpdated,
        eventPayload: mapUserStickerPacksUpdatedPayload(chatEvent.payload.type.value),
      };
    case 'chatMarkedAsUnread':
      return {
        ...generalData,
        type: EventType.ConfigGroupUnread,
        eventPayload: mapChatMarkedAsUnreadPayload(chatEvent.payload.type.value),
      };
    case 'chatMessagesTtlChanged':
      return {
        ...generalData,
        type: EventType.ChatMessagesTtlChanged,
        eventPayload: mapChatMessagesTtlChangedPayload(chatEvent.payload.type.value),
      };
    default:
      return undefined;
  }
}

export function mapGroupCounterView(chatUpdate: ChatUpdate): IGroupCounterView {
  return {
    counter: Number(chatUpdate.unreadMessagesCount),
    groupId: chatUpdate.chatState?.chatId.toString() || '',
    lastEvent: chatUpdate.lastEvent ? mapChatEvent(chatUpdate.lastEvent) : undefined,
    lastEventTs: chatUpdate.lastEventTs ? Number(chatUpdate.lastEventTs) : undefined,
    lastMessage: chatUpdate.lastMessage ? mapIMessage(chatUpdate.lastMessage) : undefined,
    lastMessageTs: chatUpdate.lastMessageTs ? Number(chatUpdate.lastMessageTs) : undefined,
    lastUserMentionTs: Number(chatUpdate.lastUserMentionTs),
    opponentSeenTs: chatUpdate.opponentSeenTs ? Number(chatUpdate.opponentSeenTs) : undefined,
    previousEventTs: chatUpdate.previousEventTs ? Number(chatUpdate.previousEventTs) : undefined,
    previousMessageTs: chatUpdate.previousMessageTs
      ? Number(chatUpdate.previousMessageTs)
      : undefined,
    seenTs: Number(chatUpdate.seenTs),
    clearHistoryTs: Number(chatUpdate.clearHistoryTs),
    updatedTs: chatUpdate.chatState?.updatedTs
      ? Number(chatUpdate.chatState?.updatedTs)
      : undefined,
    totalMessagesCount: undefined,
    unreadUserMentionsCounter: Number(chatUpdate.unreadUserMentionsCount),
  };
}
